import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { generateFirstLetterBox } from "../../utils/generateFirstLetterBox";
import { redirectToWhatsapp } from "../../utils/redirectToWhatsapp";
import { paths } from "../../routes/paths";
import FORMS from "../../api/services/FORMS";
import ASSETS from "../../api/services/ASSETS";
import DOCUMENTS from "../../api/services/DOCUMENTS";
import DocumentUploadModal from "./documentUploadModal";
import styles from "../../styles/_containers/_clients/clientCard.module.scss";
import { toast } from "react-toastify";

// ── Status helpers ────────────────────────────────────────────────────────────
const STATUS_MAP = {
  submitted: { label: "✅ חתם/ה",    color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  opened:    { label: "👁 נפתח",     color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  pending:   { label: "⏳ ממתין/ה", color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe" },
};

// Native-invoicing doc-type labels — match the dashboard documentsList screen
// and the mobile app's DocumentRow.
const DOC_TYPE_LABELS = {
  305: "חשבונית מס-קבלה",
  400: "קבלה",
  320: "חשבונית מס",
  330: "חשבונית זיכוי",
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status];
  if (!s) return null;
  return (
    <span style={{ display: "inline-block", fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, border: `1px solid ${s.border}`, borderRadius: 20, padding: "2px 10px", lineHeight: 1.6 }}>
      {s.label}
    </span>
  );
};

// ── PDF Preview Modal ─────────────────────────────────────────────────────────
const PdfPreviewModal = ({ url, onClose }) => {
  const iframeRef = useRef(null);
  const [srcdoc, setSrcdoc] = useState("");
  const [fetchError, setFetchError] = useState(false);
  const printUrl = url.replace("&noprint=1", "").replace("?noprint=1", "").replace("noprint=1&", "");

  useEffect(() => {
    fetch(url)
      .then((r) => r.text())
      .then((html) => setSrcdoc(html))
      .catch(() => setFetchError(true));
  }, [url]);

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 10000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ width: "100%", maxWidth: 860, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => iframeRef.current?.contentWindow?.print()}
            style={{ background: "#DE1468", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            🖨 הדפס / שמור PDF
          </button>
          <a href={printUrl} target="_blank" rel="noreferrer"
            style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8, padding: "8px 14px", fontSize: 14, cursor: "pointer", textDecoration: "none" }}
          >
            ↗ פתח בכרטיסייה חדשה
          </a>
        </div>
        <button onClick={onClose}
          style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8, padding: "8px 14px", fontSize: 16, cursor: "pointer" }}
        >
          ✕ סגור
        </button>
      </div>
      {fetchError ? (
        <div style={{ color: "#fff", fontSize: 14 }}>שגיאה בטעינת המסמך</div>
      ) : !srcdoc ? (
        <div style={{ color: "#fff", fontSize: 14 }}>טוען...</div>
      ) : (
        <iframe
          ref={iframeRef}
          srcdoc={srcdoc}
          style={{ width: "100%", maxWidth: 860, height: "80vh", border: "none", borderRadius: 12, background: "#fff" }}
          title="תצוגה מקדימה"
        />
      )}
    </div>
  );
};

// ── Submission Viewer Modal ───────────────────────────────────────────────────
const SubmissionViewer = ({ data, onClose, onPrint }) => {
  const { submission, form } = data;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 560, maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", direction: "rtl" }}>

        {/* Header */}
        <div style={{ background: "linear-gradient(135deg,#DE1468,#ff6b9d)", padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{form?.title}</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, marginTop: 2 }}>{submission?.clientName} · {fmt(submission?.submittedAt)}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onPrint} style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}>🖨 PDF</button>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}>✕</button>
          </div>
        </div>

        <div style={{ overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Declaration content */}
          {form?.type === "declaration" && form?.content && (
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#DE1468", fontWeight: 700, marginBottom: 6 }}>תוכן ההצהרה</div>
              <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{form.content}</div>
            </div>
          )}

          {/* Agreed */}
          {form?.type === "declaration" && (
            <div style={{ background: submission?.agreed ? "#f0fdf4" : "#fef2f2", border: `1px solid ${submission?.agreed ? "#bbf7d0" : "#fecaca"}`, borderRadius: 10, padding: "10px 14px" }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>{submission?.agreed ? "✅ הלקוח הסכים לתנאים" : "⚠️ לא אישר את התנאים"}</span>
            </div>
          )}

          {/* Questionnaire answers */}
          {form?.type === "questionnaire" && (submission?.answers || []).map((a, i) => {
            const val = Array.isArray(a.answer) ? (a.answer.length ? a.answer.join(" | ") : "—") : String(a.answer ?? "—");
            const isBool = val === "true" || val === "false";
            return (
              <div key={i} style={{ background: "#f8fafc", border: "1px solid #f1f5f9", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 4 }}>{a.questionText}</div>
                <div style={{ fontSize: 13, color: isBool ? (val === "true" ? "#16a34a" : "#dc2626") : "#1e293b" }}>
                  {isBool ? (val === "true" ? "✅ כן" : "❌ לא") : val}
                </div>
              </div>
            );
          })}

          {/* Signature */}
          {(submission?.signatureAgreed || submission?.signatureDrawData || submission?.signatureName) && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#DE1468", fontWeight: 700, marginBottom: 8 }}>חתימה</div>
              {submission?.signatureName && <div style={{ fontWeight: 700, marginBottom: 6 }}>✍ {submission.signatureName}</div>}
              {submission?.signatureAgreed && <div style={{ background: "#f0fdf4", borderRadius: 6, padding: "4px 10px", display: "inline-block", fontSize: 12, color: "#16a34a", fontWeight: 700, marginBottom: 8 }}>אישר/ה חתימה דיגיטלית</div>}
              {submission?.signatureDrawData && (
                <img src={submission.signatureDrawData} alt="חתימה" style={{ width: "100%", maxHeight: 120, objectFit: "contain", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8 }} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main ClientCard ───────────────────────────────────────────────────────────
const ClientCard = ({ id, userId, businessId, phone, name, img, setIsOpenModal }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Forms
  const [formLinks, setFormLinks]           = useState([]);
  const [formsLoading, setFormsLoading]     = useState(false);
  const [formsFetched, setFormsFetched]     = useState(false);
  const [allForms, setAllForms]             = useState([]);
  const [selectedForm, setSelectedForm]     = useState("");
  const [sendingForm, setSendingForm]       = useState(false);
  const [showSendPanel, setShowSendPanel]   = useState(false);
  const [viewingData, setViewingData]       = useState(null); // {submission, form}
  const [viewingLoading, setViewingLoading] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl]   = useState(null);

  // Documents (uploaded files — separate from form submissions).
  // Fetched alongside form-links the first time the card opens.
  const [documents, setDocuments]           = useState([]);
  const [docsLoading, setDocsLoading]       = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deletingDocId, setDeletingDocId]   = useState(null);

  // Native-invoicing documents (חשבוניות) — fetched in the same opener pass.
  // Backend resolves the customerUserId → userBusinessId server-side so we
  // don't need to thread it through here. Empty array when this customer
  // has never been billed under this business.
  const [invoices, setInvoices]             = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Fetch form links + documents when opened
  const fetchData = useCallback(async () => {
    if (formsFetched) return;
    setFormsLoading(true);
    setDocsLoading(true);
    setInvoicesLoading(true);
    try {
      const [linksRes, declRes, questRes, docsRes, invRes] = await Promise.all([
        FORMS.clientLinks(userId),
        FORMS.list("declaration"),
        FORMS.list("questionnaire"),
        ASSETS.getClientDocuments(userId).catch(() => null),
        DOCUMENTS.list({ customerUserId: userId, limit: 50 }).catch(() => null),
      ]);
      setFormLinks(linksRes?.data || []);
      setAllForms([...(declRes?.data || []), ...(questRes?.data || [])].filter(f => f.isActive));
      // saveClientDocuments / getClientDocuments use the legacy
      // {code, data} response shape (vs the {status, data} shape
      // used by the forms endpoints).
      setDocuments(docsRes?.data?.data || []);
      // Backend short-circuits with {success, data: []} when there's no
      // UserBusiness link; the normal response is {success, items: [...]}.
      setInvoices(invRes?.items || invRes?.data || []);
      setFormsFetched(true);
    } catch (e) { console.error(e); }
    finally {
      setFormsLoading(false);
      setDocsLoading(false);
      setInvoicesLoading(false);
    }
  }, [userId, formsFetched]);

  // Targeted document refetch — used after upload/delete so we
  // don't have to invalidate the whole forms+docs bundle.
  const refetchDocuments = useCallback(async () => {
    setDocsLoading(true);
    try {
      const res = await ASSETS.getClientDocuments(userId);
      setDocuments(res?.data?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setDocsLoading(false);
    }
  }, [userId]);

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`למחוק את "${doc.fileName}"?`)) return;
    setDeletingDocId(doc._id);
    try {
      const res = await ASSETS.deleteClientDocument(userId, doc._id);
      if (res?.data?.code === 200) {
        setDocuments((prev) => prev.filter((d) => d._id !== doc._id));
        toast.success("הקובץ נמחק");
      } else {
        toast.error(res?.data?.message || "שגיאה במחיקה");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "שגיאה במחיקה");
    } finally {
      setDeletingDocId(null);
    }
  };

  useEffect(() => { if (isOpen) fetchData(); }, [isOpen, fetchData]);

  const handleSendForm = async () => {
    if (!selectedForm) { toast.error("יש לבחור טופס"); return; }
    setSendingForm(true);
    try {
      await FORMS.sendLink({ formId: selectedForm, clientId: userId, clientName: name, clientPhone: phone, source: "manual" });
      toast.success("הטופס נשלח ללקוח בהצלחה 📋");
      setShowSendPanel(false);
      setSelectedForm("");
      setFormsFetched(false); // refresh
    } catch { toast.error("שגיאה בשליחת הטופס"); }
    finally { setSendingForm(false); }
  };

  const handleViewSubmission = async (link) => {
    if (!link.submissionId) return;
    const formId = link.formId?._id || link.formId;
    setViewingData(null);
    setViewingLoading(true);
    try {
      const res = await FORMS.submission(formId, link.submissionId);
      if (res?.data) setViewingData(res.data);
    } catch { toast.error("שגיאה בטעינת הטופס"); }
    finally { setViewingLoading(false); }
  };

  // Count of pending/open forms for badge
  const submittedCount = formLinks.filter(l => l.status === "submitted").length;
  const pendingCount   = formLinks.filter(l => l.status !== "submitted").length;

  return (
    <>
      <div className={`${styles.container} ${isOpen ? styles.containerOpen : ""}`}>

        {/* HEAD */}
        <div className={styles.header} onClick={() => setIsOpen(!isOpen)}>
          <div className={styles.identity}>
            <div className={styles.avatar}>
              {img ? <img src={img} alt="" /> : generateFirstLetterBox(name)}
            </div>
            <div className={styles.name}>
              <h3>{name}</h3>
              <p className={styles.mobilePhone}>{phone || "לא הוזן טלפון"}</p>
            </div>
          </div>

          <div className={styles.phoneCell}>
            <span>{phone || "לא הוזן"}</span>
          </div>

          <div className={styles.quickActions} onClick={(event) => event.stopPropagation()}>
            <a href={`tel:${phone}`} aria-label={`חיוג אל ${name}`} title="חיוג">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7.2 3.5 9.5 8l-2 2a16 16 0 0 0 6.5 6.5l2-2 4.5 2.3v2.4a2 2 0 0 1-2.2 2A17.6 17.6 0 0 1 2.8 5.7a2 2 0 0 1 2-2.2h2.4Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </a>
            <button type="button" onClick={() => redirectToWhatsapp(phone)} aria-label={`שליחת וואטסאפ אל ${name}`} title="וואטסאפ" className={styles.whatsappAction}>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 11.6a8 8 0 0 1-11.8 7L4 20l1.4-4.1A8 8 0 1 1 20 11.6Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /><path d="M9 8.5c.4 2.4 2.1 4.1 4.5 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
            </button>
            <button
              type="button"
              onClick={() => {
                localStorage.setItem("selectedClient", JSON.stringify({ userId, name }));
                navigate(paths.createAppointment);
              }}
              aria-label={`קביעת תור עבור ${name}`}
              title="קביעת תור"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 3v3M17 3v3M4 9h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><rect x="3.5" y="4.5" width="17" height="16" rx="3" stroke="currentColor" strokeWidth="1.7" /><path d="M12 12v5M9.5 14.5h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
            </button>
          </div>

          <div className={styles.profileCell} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => navigate(paths.clientMemberShip(userId))}>
              פתיחת כרטיס
            </button>
            {formLinks.length > 0 && (
              <span className={styles.formBadge} title={`${submittedCount} טפסים הושלמו, ${pendingCount} ממתינים`}>
                {formLinks.length}
              </span>
            )}
          </div>

          <div className={styles.expandCell}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setIsOpen(!isOpen);
              }}
              aria-label={isOpen ? `סגירת פרטי ${name}` : `פתיחת פרטי ${name}`}
              aria-expanded={isOpen}
            >
              <svg className={isOpen ? styles.arrowOpen : ""} viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className={`${styles.body} ${isOpen ? styles.open : styles.hide}`}>
          <hr className={styles.line1} />
          <ul>
            <a href={`tel:+972${phone}`}><li><img src="/assets/phone-icon-1.svg" alt="phone" /><p>חיוג ללקוח/ה</p></li></a>
            <li onClick={() => redirectToWhatsapp(phone)}><img src="/assets/whatsapp-icon-1.svg" alt="whatsapp" /><p>שלח הודעת וואטסאפ</p></li>
            <li onClick={() => { localStorage.setItem("selectedClient", JSON.stringify({ userId, name })); navigate(paths.createAppointment); }}>
              <img src="/assets/add-icon-1.svg" alt="add" /><p>קבע תור ללקוח</p>
            </li>
            <li onClick={() => navigate(paths.clientMemberShip(userId))}><img src="/assets/books-icon-1.svg" alt="books" /><p>כרטיס לקוח ומינויים</p></li>
            <li onClick={() => navigate(paths.clientBilling(userId))}><img src="/assets/card-icon-1.svg" alt="billing" /><p>חיובים</p></li>
            <li onClick={() => navigate(`${paths.clientBilling(userId)}?tab=accounting`)}><img src="/assets/news-icon-1.svg" alt="accounting" /><p>הנה"ח</p></li>
            <li onClick={() => navigate(paths.clientHistory(userId))}><img src="/assets/history-icon-1.svg" alt="history" /><p>היסטוריית תורים</p></li>
            <li onClick={() => navigate(`${paths.clientBilling(userId)}?tab=purchases`)}>
              <svg className={styles.actionSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 4h2l2.1 10.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L20 8H6M10 20h.01M17 20h.01" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg><p>רכישות אחרונות</p>
            </li>
            <li onClick={() => navigate(`${paths.reminders}?client=${userId}&name=${encodeURIComponent(name)}`)}>
              <svg className={styles.actionSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg><p>תזכורות</p>
            </li>
            <li onClick={() => setIsOpenModal(userId)}><img src="/assets/remove-icon-1.svg" alt="remove" /><p>מחיקת לקוח/ה</p></li>
          </ul>

          {/* ── Documents / Forms Section ── */}
          <div style={{ padding: "14px 4px 4px", direction: "rtl" }}>

            {/* Section title + badge */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>📄 מסמכים, אישורים והצהרות</span>
              {formsLoading
                ? <span style={{ fontSize: 12, color: "#94a3b8" }}>טוען...</span>
                : formLinks.length > 0 && (
                  <span style={{ background: "#f1f5f9", borderRadius: 20, padding: "2px 10px", fontSize: 12, color: "#475569", fontWeight: 700 }}>
                    {formLinks.length} {formLinks.length === 1 ? "טופס" : "טפסים"}
                  </span>
                )
              }
            </div>

            {/* Form links list */}
            {!formsLoading && formLinks.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                {formLinks.map((link) => {
                  const title = link.formId?.title || "טופס";
                  const type  = link.formId?.type;
                  const icon  = type === "declaration" ? "📋" : "📝";
                  const fmt   = (d) => d ? new Date(d).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "";
                  const hasAlerts = !!link.hasAlerts && (link.alertCount || 0) > 0;
                  return (
                    <div
                      key={link._id}
                      style={{
                        background: hasAlerts ? "#fef2f2" : "#f8fafc",
                        border: hasAlerts ? "1px solid #fecaca" : "1px solid #e2e8f0",
                        borderRadius: 10,
                        padding: "10px 14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: hasAlerts ? "#b91c1c" : "#1e293b",
                          }}
                        >
                          {hasAlerts && "⚠️ "}{icon} {title}
                        </div>
                        <div style={{ fontSize: 11, color: hasAlerts ? "#b91c1c" : "#94a3b8", marginTop: 2 }}>
                          {hasAlerts
                            ? `${link.alertCount} תשובות רגישות · ${fmt(link.sentAt)}`
                            : fmt(link.sentAt)}
                        </div>
                      </div>
                      <StatusBadge status={link.status} />
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        {link.status === "submitted" && (
                          <button
                            onClick={() => handleViewSubmission(link)}
                            style={{ background: "#f1f5f9", border: "none", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", color: "#475569" }}
                          >
                            👁 צפייה
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            setSendingForm(true);
                            try {
                              await FORMS.sendLink({ formId: link.formId?._id || link.formId, clientId: userId, clientName: name, clientPhone: phone, source: "manual" });
                              toast.success("הטופס נשלח שוב ✅");
                              setFormsFetched(false);
                            } catch { toast.error("שגיאה"); }
                            finally { setSendingForm(false); }
                          }}
                          style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", color: "#DE1468" }}
                        >
                          📤 שלח שוב
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!formsLoading && formLinks.length === 0 && (
              <p style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "8px 0 4px" }}>לא נשלחו טפסים עדיין</p>
            )}

            {/* ── חשבוניות (native-invoicing documents) ──────────────────
                 Auto-hidden when this customer has no issued invoices and
                 no in-flight load — existing clients see no visual change. */}
            {(invoicesLoading || invoices.length > 0) && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
                    🧾 חשבוניות
                  </span>
                  {invoicesLoading ? (
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>טוען...</span>
                  ) : invoices.length > 0 ? (
                    <span style={{ background: "#fce7f3", borderRadius: 20, padding: "2px 10px", fontSize: 12, color: "#9d174d", fontWeight: 700 }}>
                      {invoices.length} {invoices.length === 1 ? "מסמך" : "מסמכים"}
                    </span>
                  ) : null}
                </div>

                {!invoicesLoading && invoices.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                    {invoices.map((d) => {
                      const fmt = (n) => "₪" + (Number(n) || 0).toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      const fmtDate = (date) => date ? new Date(date).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "";
                      const typeLabel = DOC_TYPE_LABELS[d.docType] || `סוג ${d.docType}`;
                      const isCredited = d.status === "cancelled_by_credit";
                      const url = d.shortUrl || d.pdfUrl || "";
                      return (
                        <div
                          key={d._id}
                          style={{
                            background: isCredited ? "#fafafa" : "#fef2f7",
                            border: `1px solid ${isCredited ? "#e2e8f0" : "#fbcfe8"}`,
                            borderRadius: 10,
                            padding: "10px 14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            opacity: isCredited ? 0.65 : 1,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {typeLabel} #{d.number ?? "—"}
                              {isCredited && (
                                <span style={{ marginInlineStart: 6, background: "#fef3c7", color: "#92400e", borderRadius: 8, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>
                                  זוכה
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                              {fmtDate(d.issuedAt || d.createdAt)} · {fmt(d.total)}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            {url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ background: "#f1f5f9", border: "none", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 700, color: "#475569", textDecoration: "none", cursor: "pointer" }}
                              >
                                👁 צפייה
                              </a>
                            ) : null}
                            {url && (
                              <button
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(url);
                                    toast.success("הקישור הועתק");
                                  } catch { toast.error("העתקה נכשלה"); }
                                }}
                                style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 7, padding: "5px 10px", fontSize: 12, cursor: "pointer" }}
                                title="העתק קישור"
                              >
                                📋
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Uploaded documents list ──────────────────────────────── */}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
                  📁 קבצים שהועלו
                </span>
                {docsLoading ? (
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>טוען...</span>
                ) : documents.length > 0 ? (
                  <span style={{ background: "#f1f5f9", borderRadius: 20, padding: "2px 10px", fontSize: 12, color: "#475569", fontWeight: 700 }}>
                    {documents.length} {documents.length === 1 ? "קובץ" : "קבצים"}
                  </span>
                ) : null}
              </div>

              {!docsLoading && documents.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                  {documents.map((doc) => {
                    const fmt = (d) => d ? new Date(d).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "";
                    const ext = (doc.s3FilePath || "").split(".").pop()?.toLowerCase() || "";
                    const icon = ["jpg","jpeg","png","gif","heic","webp"].includes(ext) ? "🖼" : ext === "pdf" ? "📕" : ["doc","docx"].includes(ext) ? "📘" : ["xls","xlsx"].includes(ext) ? "📗" : "📄";
                    return (
                      <div
                        key={doc._id}
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: "10px 14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#1e293b",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {icon} {doc.fileName || "קובץ"}
                          </div>
                          {doc.comment ? (
                            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {doc.comment}
                            </div>
                          ) : null}
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                            {fmt(doc.uploadDate || doc.modifiedDate)}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              background: "#f1f5f9",
                              border: "none",
                              borderRadius: 7,
                              padding: "5px 10px",
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#475569",
                              textDecoration: "none",
                              cursor: "pointer",
                            }}
                          >
                            👁 צפייה
                          </a>
                          <button
                            onClick={() => handleDeleteDocument(doc)}
                            disabled={deletingDocId === doc._id}
                            style={{
                              background: "#fff",
                              border: "1px solid #fecaca",
                              borderRadius: 7,
                              padding: "5px 10px",
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#dc2626",
                              cursor: deletingDocId === doc._id ? "not-allowed" : "pointer",
                              opacity: deletingDocId === doc._id ? 0.5 : 1,
                            }}
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!docsLoading && documents.length === 0 && (
                <p style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "8px 0 4px" }}>
                  אין קבצים שהועלו עדיין
                </p>
              )}
            </div>

            {/* CTAs row — both actions side by side, mirroring Admin-App */}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button
                onClick={() => setShowSendPanel(!showSendPanel)}
                style={{ flex: 1, background: showSendPanel ? "#fff9f9" : "#fff", border: "1.5px dashed #DE1468", borderRadius: 10, padding: "9px 0", fontSize: 13, fontWeight: 700, color: "#DE1468", cursor: "pointer" }}
              >
                {showSendPanel ? "✕ ביטול" : "+ שלח טופס חדש"}
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                style={{ flex: 1, background: "#fff", border: "1.5px dashed #6366f1", borderRadius: 10, padding: "9px 0", fontSize: 13, fontWeight: 700, color: "#6366f1", cursor: "pointer" }}
              >
                ☁ העלאת קובץ
              </button>
            </div>

            {showSendPanel && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                <select
                  value={selectedForm}
                  onChange={(e) => setSelectedForm(e.target.value)}
                  style={{ border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 13, direction: "rtl", outline: "none", fontFamily: "inherit" }}
                >
                  <option value="">-- בחר טופס/שאלון --</option>
                  {allForms.map((f) => (
                    <option key={f._id} value={f._id}>{f.type === "declaration" ? "📋" : "📝"} {f.title}</option>
                  ))}
                </select>
                <button
                  onClick={handleSendForm}
                  disabled={!selectedForm || sendingForm}
                  style={{ background: "#DE1468", color: "#fff", border: "none", borderRadius: 8, padding: "9px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: !selectedForm || sendingForm ? 0.6 : 1 }}
                >
                  {sendingForm ? "שולח..." : "📤 שלח"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submission Viewer */}
      {viewingLoading && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 32, fontSize: 14, color: "#374151" }}>טוען...</div>
        </div>
      )}
      {viewingData && (
        <SubmissionViewer
          data={viewingData}
          onClose={() => setViewingData(null)}
          onPrint={() => {
            const formId = viewingData.form?._id;
            const subId  = viewingData.submission?._id;
            if (formId && subId) setPdfPreviewUrl(FORMS.previewUrl(formId, subId));
          }}
        />
      )}
      {pdfPreviewUrl && <PdfPreviewModal url={pdfPreviewUrl} onClose={() => setPdfPreviewUrl(null)} />}

      {/* Document upload modal — controlled by the "☁ העלאת קובץ"
          CTA in the documents section. On success it triggers a
          targeted refetch of the documents list. */}
      {showUploadModal && (
        <DocumentUploadModal
          userId={userId}
          onClose={() => setShowUploadModal(false)}
          onUploaded={refetchDocuments}
        />
      )}
    </>
  );
};

export default ClientCard;
